/**
 * Properties repository — the ONLY place that touches `properties` data.
 *
 * Hooks (`useProperties`, `useProperty`, …) and components call these functions;
 * nothing else may call `supabase.from("properties")` directly. Follows the
 * Strangler-Fig per-slice migration template established by
 * `serviceRequests`/`receipts` (PR #1649, see the handoff doc):
 *   Step 1 (now)   — consolidate scattered supabase calls here (Supabase-backed).
 *   Step 2 (later) — build guarded NestJS endpoints.
 *   Step 3 (flip)  — swap the impls below to the Nest client via API_ROUTES.
 *
 * Every read/write is company-scoped. The employee-assignment scope (Layer 2)
 * is expressed by the optional `assignedIds` filter, which the calling hook
 * computes from `employee_property_assignments` (a different resource).
 */
import { z } from "zod";
import {
  ownerPortalPropertyDetailSchema,
  propertyCardSchema,
  propertyRowSchema,
  propertyStatsSchema,
  type OwnerPortalPropertyDetail,
  type OwnerUnitDisplayStatus,
} from "@dallali/shared";
import { parseImages } from "@/lib/propertyImages";
import { supabase } from "@/integrations/supabase/client";
import type { Database, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { serializeError } from "@/lib/sentry";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { fireAndForgetNestPost, makeNestRequest } from "@/services/nestRequest";
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { categorizeProperty, type PropertyCategory } from "@/lib/propertyCategory";

/**
 * A unit as sent to the owner-callable RPCs. Numeric fields stay strings: they
 * come straight from form inputs and the RPC casts them (`nullif(...)::numeric`),
 * so "" means "not provided" rather than 0.
 */
export interface OwnerUnitInput {
  unit_number: string;
  unit_type?: string;
  purpose?: string | null;
  area?: string;
  floor_number?: string;
  bedrooms?: string;
  bathrooms?: string;
  images?: string[];
}
import { OWNER_LITE_COLUMNS, type OwnerLite } from "@/lib/ownerHelpers";

const TABLE = "properties";

// Authenticated Nest request bound to the "properties" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
// UNSCOPED: no x-active-role forwarded/required. Used by writes (create/update/
// softDelete) — WRITE endpoints do NOT carry RowScopeGuard (capability-gated
// only, see properties.controller.ts), so they must never be blocked or
// rerouted to the Supabase fallback by a missing/unresolved active role (issue
// #3278 — a client-side guard on a shared instance previously did that).
const nestRequest = makeNestRequest("properties");

// Row-scoped variant: /properties cards/getById/stats ARE RowScopeGuard-protected
// (active-role row-scope, ADR-0001) and 403 without the header — mirror the
// Supabase client which injects x-active-role from sessionStorage on every call,
// and short-circuit the boot-race window client-side (`requireActiveRole`).
const nestRequestScoped = makeNestRequest("properties", {
  forwardActiveRole: true,
  requireActiveRole: true,
});

// Owner portal: OwnerPortalGuard 403s without `x-active-role: owner`.
const nestRequestOwner = makeNestRequest("owner-portal", {
  forwardActiveRole: true,
  requireActiveRole: true,
});

type OwnerDetailRow = NonNullable<
  Awaited<ReturnType<(typeof propertiesRepository)["getByIdForOwner"]>>
>;

const FALLBACK_UNIT_STATUS: Record<string, OwnerUnitDisplayStatus> = {
  rented: "rented",
  maintenance: "maintenance",
  reserved: "reserved",
  sold: "sold",
};

/** Map the Supabase owner detail read into the Nest response shape. */
export function ownerDetailFromSupabase(row: OwnerDetailRow): OwnerPortalPropertyDetail {
  const units = [...(row.units ?? [])].sort((a, b) =>
    String(a.unit_number).localeCompare(String(b.unit_number), undefined, { numeric: true }),
  );
  const statuses = units.map((u) => FALLBACK_UNIT_STATUS[u.status ?? ""] ?? "vacant");
  const count = (s: OwnerUnitDisplayStatus) => statuses.filter((x) => x === s).length;
  const rented = count("rented");
  const images = parseImages(row.images);
  return {
    today: new Date().toISOString().slice(0, 10),
    property: {
      id: row.id,
      name: row.name,
      name_en: row.name_en ?? null,
      property_type: row.property_type ?? "",
      purpose: row.purpose ?? null,
      city: row.city ?? null,
      city_en: null,
      district: row.district ?? null,
      district_en: null,
      address: row.address ?? null,
      address_en: null,
      image_url: images[0] ?? null,
      description: row.description ?? null,
      description_en: null,
      total_area: row.total_area ?? null,
      built_year: row.built_year ?? null,
      floors_count: row.floors_count ?? null,
      deed_number: row.deed_number ?? null,
      images,
      occupancy: {
        unit_count: units.length,
        rented,
        vacant: count("vacant"),
        marketing: 0,
        maintenance: count("maintenance"),
        reserved: count("reserved"),
        sold: count("sold"),
        late_rent: 0,
        expiring_soon: 0,
        occupancy_rate: units.length > 0 ? Math.round((rented / units.length) * 100) : 0,
      },
    },
    units: units.map((u, i) => {
      const unitImages = parseImages(u.images);
      return {
        id: u.id,
        property_id: row.id,
        unit_number: u.unit_number,
        unit_type: u.unit_type ?? "",
        purpose: null,
        floor_number: u.floor_number ?? null,
        area: u.area ?? null,
        bedrooms: u.bedrooms ?? null,
        bathrooms: u.bathrooms ?? null,
        rent_amount: u.rent_amount ?? null,
        annual_rent: null,
        is_furnished: u.is_furnished ?? null,
        image_url: unitImages[0] ?? null,
        images: unitImages,
        unit_status: u.status ?? "",
        status: statuses[i],
        contract_end_date: null,
        days_to_end: null,
        tenant_name: null,
        tenant_name_en: null,
      };
    }),
    collection: {
      active_contracts: 0,
      total_amount: 0,
      collected_amount: 0,
      overdue_amount: 0,
      overdue_count: 0,
    },
    open_maintenance: [],
  };
}

/**
 * Property `purpose` values. The DB column is a free `string` (no PG enum), but
 * these are the only values the app writes — see `statsByPurpose` and the V5
 * mapping. Typing the count helper to this union gives call-site autocomplete
 * and catches typos.
 */
export type PropertyPurpose =
  | "residential"
  | "commercial"
  | "industrial"
  | "agricultural"
  | "multi_purpose";

/**
 * Select used by the canonical list — only the columns consumed by list/card
 * consumers (Properties.tsx, PropertyCardEnhanced, PropertyCard,
 * PropertiesTableView, PropertiesFullMap, EmployeeProperties).  The heavy
 * `amenities` JSONB (RER snapshots), detail-only text fields, and translation
 * metadata are excluded — they belong on DETAIL_SELECT / getById.
 *
 * Embedded relations carry what the card aggregates derive from (#3243:
 * is_active/status/rent_amount for unitsCount/occupancy/income) + unit images
 * (card fallback) + owner (getPropertyCardOwnerLabel multi-owner detection)
 * + property owner (single-owner card label).
 */
const LIST_SELECT = `id, name, name_en, address, city, district, images, latitude, longitude, property_type, purpose, created_at, deed_number, rer_number, units_count, units(is_active, status, rent_amount, images, owner:owners(${OWNER_LITE_COLUMNS})), owner:owners(${OWNER_LITE_COLUMNS})`;

/** Select used for a single property — property with its owner. */
const DETAIL_SELECT = `*, owner:owners(${OWNER_LITE_COLUMNS})`;

/** Owner-portal list — property + the unit fields the owner cards/detail read. */
const OWNER_LIST_SELECT =
  "*, units:units(id, unit_number, status, rent_amount, area, bedrooms, bathrooms, images)";

/** Owner-portal detail — adds the unit fields the detail table needs. */
const OWNER_DETAIL_SELECT =
  "*, units:units(id, unit_number, unit_type, status, rent_amount, area, bedrooms, bathrooms, floor_number, is_furnished, images)";

/** Owner-portal occupancy — minimal property + unit-status only. */
const OWNER_OCCUPANCY_SELECT = "id, name, name_en, units:units(id, status)";

interface ListOptions {
  companyId: string;
  /**
   * Employee Layer-2 scope. `null`/`undefined` → not an employee, no id filter.
   * A non-empty array → restrict to those property ids. (An empty array is
   * handled by the caller, which short-circuits to an empty result.)
   */
  assignedIds?: string[] | null;
  /** Optional created_at bounds (ISO) — statsByPurpose only. Unused elsewhere. */
  startDate?: string;
  endDate?: string;
}

/** Supabase list implementation — the employee-scoped live path + the fallback. */
async function listViaSupabase({ companyId, assignedIds }: ListOptions) {
  let query = supabase
    .from(TABLE)
    .select(LIST_SELECT)
    .eq("company_id", companyId)
    .is("deleted_at", null);

  if (assignedIds && assignedIds.length > 0) {
    query = query.in("id", assignedIds);
  }

  const { data, error } = await query.order("created_at", { ascending: false });
  if (error) throw error;
  // SELECT is a projection subset of the full Row — the cast widens back so
  // every consumer's inferred types (PropertyWithStats, cards, table) type-check.
  return data as unknown as ListRow[];
}

/**
 * Full property list row type — the DB Row type plus the embedded relations
 * the SELECT carries (units + owner).  LIST_SELECT is a runtime projection
 * subset; the cast in listViaSupabase widens back so every consumer
 * (PropertyWithStats, cards, table) type-checks against the same shape.
 */
type ListRow = Database["public"]["Tables"]["properties"]["Row"] & {
  units: { images: unknown; owner: OwnerLite | null }[];
  owner: OwnerLite | null;
};

/** Supabase create implementation — fallback for the flipped path. */
async function createViaSupabase(payload: TablesInsert<"properties">) {
  const { data, error } = await supabase
    .from(TABLE)
    .insert(payload)
    .select()
    .maybeSingle();
  if (error) throw error;
  // An insert that returns no row is abnormal — surface it rather than
  // handing the caller undefined (and avoid .single()'s PGRST116 throw).
  if (!data) throw new Error("properties insert returned no row");
  return data;
}

/** Supabase update implementation — fallback for the flipped path. */
async function updateViaSupabase(
  id: string,
  patch: TablesUpdate<"properties">,
  companyId: string,
) {
  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq("id", id)
    .eq("company_id", companyId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Supabase getById implementation — fallback for the flipped path. */
async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from(TABLE)
    .select(DETAIL_SELECT)
    .eq("id", id)
    .eq("company_id", companyId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export const propertiesRepository = {
  /**
   * Company-scoped list (non-deleted), newest first, optionally employee-scoped.
   *
   * FLIPPED to the cached Nest cards endpoint (`GET /properties/cards`,
   * docs/design/cache-layer.md Phase 2) for company-wide staff reads. The card
   * is a SUPERSET of LIST_SELECT (`*, units(images, owner), owner` + occupancy
   * counts), so existing consumers read it unchanged.
   *
   * Employee Layer-2 scope (`assignedIds`) stays on the Supabase path: the
   * guarded endpoint serves the WHOLE company (per-employee property scope is
   * not yet server-side — see PERMISSIONS.md "NOT yet done"), so serving an
   * employee from it would widen their visibility. Supabase remains the
   * fallback for the flipped path (loud — reportNestFallback).
   */
  async list({ companyId, assignedIds }: ListOptions) {
    const employeeScoped = assignedIds != null;
    if (API_URL && !employeeScoped) {
      try {
        const rows = await nestRequestScoped<unknown[]>(API_ROUTES.properties.cards());
        const cards = z.array(propertyCardSchema).parse(rows);
        // Structural superset of the Supabase LIST_SELECT row (gated by the
        // parse above) — cast keeps every consumer's inferred types intact.
        return cards as unknown as ListRow[];
      } catch (err) {
        reportNestFallback("properties.list", err);
      }
    }
    return listViaSupabase({ companyId, assignedIds });
  },

  /**
   * Single property by id, company-scoped.
   *
   * FLIPPED to the guarded Nest endpoint (Track-E: GET /properties/:id —
   * enables eventual RLS retirement for this read). Returns the same shape as
   * the Supabase DETAIL_SELECT (`*, owner:owners(id, full_name, full_name_en)`).
   * Supabase remains the loud fallback (reversible unit).
   */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequestScoped<unknown>(API_ROUTES.properties.getById(id));
        return propertyRowSchema.parse(row) as unknown as Awaited<ReturnType<typeof getByIdViaSupabase>>;
      } catch (err) {
        // Deterministic 404 = the property is genuinely absent / soft-deleted /
        // cross-tenant / row-scoped out. The Supabase fallback (company_id
        // filter only, no row-scope) would return null anyway — answer null
        // directly, no fallback round-trip, no nest-fallback noise (#3394).
        if (isDefinitiveNotFound(err)) {
          return null as Awaited<ReturnType<typeof getByIdViaSupabase>>;
        }
        reportNestFallback("properties.getById", err);
      }
    }
    return getByIdViaSupabase(id, companyId);
  },

  /**
   * Insert a property; returns the row.
   *
   * FLIPPED to the guarded Nest endpoint (Phase 2 write-ownership gate — the
   * property_card cache invalidates inside the Nest write path). The endpoint
   * derives `company_id` from the JWT; the body's value is ignored server-side.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the Supabase
   * fallback only runs when Nest verifiably did not commit (safe 4xx /
   * unconfigured). An ambiguous outcome (network, timeout, 5xx, parse failure
   * after a 2xx) RETHROWS instead of re-issuing the insert — a fallback after a
   * committed-but-lost Nest write would double-create the property.
   */
  async create(payload: TablesInsert<"properties">) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.properties.create(), {
          method: "POST",
          body: JSON.stringify(payload),
        });
        return propertyRowSchema.parse(row) as unknown as Awaited<
          ReturnType<typeof createViaSupabase>
        >;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("properties.create", err);
      }
    }
    return createViaSupabase(payload);
  },

  /**
   * Owner-portal: the owner's (non-deleted) properties with unit thumbnails,
   * scoped by owner AND company. Backs `useOwnerProperties`. Supabase-only for
   * now — there is no owner-scoped Nest endpoint yet (Strangler-Fig Step 1).
   *
   * Filters `deleted_at IS NULL` (soft-delete), NOT `is_active`: per the
   * system-wide convention (src/__tests__/soft-delete-filters.test.ts) the
   * live/not-live signal for a *property* is soft-delete. `is_active` is a
   * unit-level status flag — vestigial on properties — so every property read
   * (staff `listViaSupabase`, reports, owner aggregates) uses `deleted_at`.
   */
  /**
   * Owner adds a property DIRECTLY (Pkg 1). RLS blocks an owner from inserting
   * into `properties`, so this goes through the owner-callable SECURITY DEFINER
   * RPC (mig 20260716120000) that derives owner_id from auth.uid(). Returns the
   * new property id. Live immediately — no admin approval (CEO 2026-07).
   */
  async ownerCreateProperty(params: {
    companyId: string;
    name: string;
    nameEn?: string | null;
    purpose?: string | null;
    propertyType?: string;
    totalArea?: number | null;
    address?: string | null;
    city?: string | null;
    district?: string | null;
    images?: string[];
    documents?: string[];
    /** Created in the same transaction — a 0-unit property is unusable. */
    units?: OwnerUnitInput[];
  }): Promise<string> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("owner_create_property", {
      p_company_id: params.companyId,
      p_name: params.name,
      p_name_en: params.nameEn ?? null,
      p_purpose: params.purpose ?? null,
      p_property_type: params.propertyType ?? "apartment",
      p_total_area: params.totalArea ?? null,
      p_address: params.address ?? null,
      p_city: params.city ?? null,
      p_district: params.district ?? null,
      p_images: params.images ?? [],
      p_documents: params.documents ?? [],
      p_units: params.units ?? [],
    });
    if (error) throw error;
    return data as string;
  },

  /** Add a unit to a property the caller already owns. */
  async ownerAddUnit(params: { propertyId: string } & OwnerUnitInput): Promise<string> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)("owner_add_unit", {
      p_property_id: params.propertyId,
      p_unit_number: params.unit_number,
      p_unit_type: params.unit_type ?? "apartment",
      p_purpose: params.purpose ?? null,
      p_area: params.area ? Number(params.area) : null,
      p_floor_number: params.floor_number ? Number(params.floor_number) : null,
      p_bedrooms: params.bedrooms ? Number(params.bedrooms) : null,
      p_bathrooms: params.bathrooms ? Number(params.bathrooms) : null,
      p_images: params.images ?? [],
    });
    if (error) throw error;
    return data as string;
  },

  async listForOwner(ownerId: string, companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(OWNER_LIST_SELECT)
      .eq("owner_id", ownerId)
      .eq("company_id", companyId)
      .is("deleted_at", null);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Owner-portal: a single (non-deleted) property the owner owns, scoped by id,
   * owner AND company. The Supabase fallback of `getOwnerPortalDetail`. Returns null when the
   * property isn't this owner's, isn't this company's, or is soft-deleted.
   */
  async getByIdForOwner(id: string, ownerId: string, companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(OWNER_DETAIL_SELECT)
      .eq("id", id)
      .eq("owner_id", ownerId)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /**
   * Owner-portal property screen — Nest-first (`GET /owner-portal/properties/:id`:
   * units of the property regardless of `units.owner_id`, tenants, collection,
   * open maintenance). The Supabase fallback maps `getByIdForOwner` into the
   * same shape with what RLS lets it read (no tenants/collection/maintenance).
   * Returns null when the property isn't the owner's.
   */
  async getOwnerPortalDetail(
    id: string,
    ownerId: string,
    companyId: string,
  ): Promise<OwnerPortalPropertyDetail | null> {
    if (API_URL) {
      try {
        const res = await nestRequestOwner<unknown>(API_ROUTES.ownerPortal.property(id));
        return ownerPortalPropertyDetailSchema.parse(res) as OwnerPortalPropertyDetail;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null;
        reportNestFallback("properties.getOwnerPortalDetail", err);
      }
    }
    const row = await propertiesRepository.getByIdForOwner(id, ownerId, companyId);
    return row ? ownerDetailFromSupabase(row) : null;
  },

  /**
   * Owner-portal occupancy: the owner's (non-deleted) properties via `owner_id`,
   * scoped by company. Backs `useOwnerOccupancy`'s primary lookup.
   */
  async listOccupancyForOwner(ownerId: string, companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(OWNER_OCCUPANCY_SELECT)
      .eq("owner_id", ownerId)
      .eq("company_id", companyId)
      .is("deleted_at", null);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Owner-portal occupancy fallback: (non-deleted) properties by id (derived
   * from the owner's contracts), scoped by company. Both `company_id` AND
   * `deleted_at IS NULL` are required here too — the invariants are "every
   * properties read is company-scoped" and "soft-deleted properties never
   * count", even when the ids come from an already-scoped contracts query.
   */
  async listOccupancyByIds(ids: string[], companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(OWNER_OCCUPANCY_SELECT)
      .in("id", ids)
      .eq("company_id", companyId)
      .is("deleted_at", null);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * True if THIS owner already has a property with this deed (company-scoped).
   *
   * Owner-scoped on purpose: a deed registered under a *different* owner (or an
   * orphaned NULL-owner row) must NOT block a new owner's RER import — otherwise
   * the owner lands on an empty dashboard. `limit(1)` keeps `maybeSingle()` from
   * erroring when more than one row shares the deed.
   */
  async existsByDeedAndOwner(deedNumber: string, ownerId: string, companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id")
      .eq("deed_number", deedNumber)
      .eq("company_id", companyId)
      .eq("owner_id", ownerId)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data !== null;
  },

  /**
   * Properties that HAVE an owner, as `{ id, name, owner_id }` — the
   * owner-payouts property↔owner join set. Company-scoped. KEPT on Supabase —
   * batched finance read. NEVER throws: the pre-consolidation site
   * destructured `{ data }` only (a failed read yields zero-amount payout
   * rows), so an error resolves to [].
   */
  async listWithOwner(
    companyId: string,
  ): Promise<{ id: string; name: string; owner_id: string | null }[]> {
    const { data } = await supabase
      .from(TABLE)
      .select("id, name, owner_id")
      .eq("company_id", companyId)
      .not("owner_id", "is", null);
    return data ?? [];
  },

  /**
   * `{ id, name, name_en }` of non-deleted properties (occupancy-report
   * breakdown). Company-scoped. NEVER throws: the pre-consolidation site
   * destructured `{ data }` only (breakdown renders empty), so an error
   * resolves to [].
   */
  async listNamesForReport(
    companyId: string,
  ): Promise<{ id: string; name: string; name_en: string | null }[]> {
    const { data } = await supabase
      .from(TABLE)
      .select("id, name, name_en")
      .eq("company_id", companyId)
      .is("deleted_at", null);
    return data ?? [];
  },

  /**
   * Non-deleted property count for the reports dashboard tile. Company-
   * scoped. NEVER throws: the pre-consolidation site read `res.count` without
   * an error check (tile falls back to 0), so an error resolves to null.
   */
  async countForReports(companyId: string): Promise<number | null> {
    const { count } = await supabase
      .from(TABLE)
      .select("id", { count: "exact" })
      .eq("company_id", companyId)
      .is("deleted_at", null);
    return count ?? null;
  },

  /**
   * `{ id, name, property_type }` rows for the property-performance report
   * (deliberately no soft-delete filter — verbatim from the report page).
   * Company-scoped. NEVER throws: the pre-consolidation site read `res.data`
   * without an error check (report renders empty), so an error resolves
   * to [].
   */
  async listPerformanceRows(
    companyId: string,
  ): Promise<{ id: string; name: string; property_type: string | null }[]> {
    const { data } = await supabase
      .from(TABLE)
      .select("id, name, property_type")
      .eq("company_id", companyId);
    return data ?? [];
  },

  /**
   * A property's owner_id, company-scoped (task-report WhatsApp share).
   * NEVER throws: the pre-consolidation site destructured `{ data }` only
   * (share falls back to the no-phone toast), so an error resolves to null.
   */
  async getOwnerId(
    propertyId: string,
    companyId: string,
  ): Promise<{ owner_id: string | null } | null> {
    const { data } = await supabase
      .from(TABLE)
      .select("owner_id")
      .eq("id", propertyId)
      .eq("company_id", companyId)
      .maybeSingle();
    return data;
  },

  /**
   * Properties (with their unit rows) directly linked to any of a person's
   * owner-row fragments — the owner-details portfolio base set. Company-
   * scoped; throws on error. KEPT on Supabase.
   */
  async listWithUnitsByOwnerIds(companyId: string, ownerIds: string[]) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(
        `*, units:units(id, unit_number, status, rent_amount)`,
      )
      .eq("company_id", companyId)
      .in("owner_id", ownerIds);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * `{ id }` of properties directly linked to any of a person's owner-row
   * fragments (owner financial summary). Company-scoped; throws on error.
   */
  async listIdsByOwnerIds(
    companyId: string,
    ownerIds: string[],
  ): Promise<{ id: string }[]> {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id")
      .eq("company_id", companyId)
      .in("owner_id", ownerIds);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Full property rows for a set of ids (multi-owner properties resolved via
   * unit-level ownership). Company-scoped; throws on error.
   */
  async listByIds(companyId: string, ids: string[]) {
    const { data, error } = await supabase
      .from(TABLE)
      .select("*")
      .eq("company_id", companyId)
      .in("id", ids);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Full property row by id, RLS-scoped only (verbatim from the
   * contract-details page — `contract.property_id` is already
   * company-validated by the company-scoped contract read upstream).
   * Throws on error; null when absent.
   */
  async getByIdRlsScoped(id: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /**
   * Active properties that have NO owner yet (the AddOwnerDialog link
   * picker). Company-scoped; throws on error.
   */
  async listWithoutOwner(
    companyId: string,
  ): Promise<{ id: string; name: string; name_en: string | null }[]> {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id, name, name_en")
      .eq("company_id", companyId)
      .is("owner_id", null)
      .eq("is_active", true);
    if (error) throw error;
    return data || [];
  },

  /**
   * Oldest LIVE (non-deleted) property with this deed number — the
   * gov-import resolve step. Duplicate-tolerant: `.limit(1)` + oldest-first
   * means two live duplicates can never surface a multiple-rows error.
   * Company-scoped; throws the wrapped lookup error verbatim.
   */
  async findOldestLiveByDeed(
    companyId: string,
    deedNumber: string,
  ): Promise<{ id: string; name: string | null } | null> {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id, name")
      .eq("company_id", companyId)
      .eq("deed_number", deedNumber)
      .is("deleted_at", null)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(`Property lookup failed: ${error.message}`);
    return data;
  },

  /**
   * Active property with this deed number (REGA wizard match-by-deed —
   * lookup only, never mutates). Company-scoped. NEVER throws: the
   * pre-consolidation site destructured `{ data }` only (no match ⇒ the
   * wizard proceeds to create), so an error resolves to null.
   */
  async findActiveByDeed(
    companyId: string,
    deedNumber: string,
  ): Promise<{ id: string; name: string | null } | null> {
    const { data } = await supabase
      .from(TABLE)
      .select("id, name")
      .eq("company_id", companyId)
      .eq("deed_number", deedNumber)
      .eq("is_active", true)
      .maybeSingle();
    return data;
  },

  /**
   * Bare id of any property with this deed number (owner-dashboard RER
   * auto-import idempotency check — includes soft-deleted rows, exactly as
   * the inline original did). Company-scoped. NEVER throws: the
   * pre-consolidation site destructured `{ data }` only (a failed check just
   * re-attempts the insert; the DB constraint is the real guard).
   */
  async findIdByDeed(
    companyId: string,
    deedNumber: string,
  ): Promise<{ id: string } | null> {
    const { data } = await supabase
      .from(TABLE)
      .select("id")
      .eq("deed_number", deedNumber)
      .eq("company_id", companyId)
      .maybeSingle();
    return data;
  },

  /**
   * Insert an RER-imported property and return its id (owner-dashboard
   * auto-import; the row already carries company_id/owner_id/deed_number and
   * the RER snapshot under amenities). Runs under the CALLER's session — the
   * owner persona inserts via its own RLS insert policy (a staff endpoint
   * would 403 the self-signing owner, #2738). Throws on error; the import
   * loop captures and skips the property.
   */
  async createFromRerImport(
    row: TablesInsert<"properties">,
    companyId: string,
  ): Promise<{ id: string }> {
    const { data, error } = await supabase
      .from(TABLE)
      // company_id is re-stamped here rather than trusted from the caller's
      // row — the repository is the tenancy boundary for this write.
      .insert({ ...row, company_id: companyId })
      .select("id")
      .maybeSingle();
    if (error) throw error;
    // No row means the insert's RLS read-back was filtered, not that the
    // write failed — surface that plainly instead of PGRST116.
    if (!data) throw new Error("properties insert returned no row");
    return data;
  },

  /**
   * Properties (with unit rows) for a set of ids, RLS-scoped only (verbatim
   * from the employee-portal read — the ids come from the employee's own
   * company-scoped assignments). Throws on error.
   */
  async listWithUnitsByIdsRlsScoped(ids: string[]) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(`*, units:units(id, unit_number, status, floor_number, area)`)
      .in("id", ids);
    if (error) throw error;
    return data || [];
  },

  /**
   * `{ id, name, name_en, deed_number }` name-ordered lookup rows for the
   * add-payment dialog's property dropdown. Company-scoped; throws on error.
   */
  async listForPaymentDialog(
    companyId: string,
  ): Promise<
    { id: string; name: string; name_en: string | null; deed_number: string | null }[]
  > {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id, name, name_en, deed_number")
      .eq("company_id", companyId)
      .order("name");
    if (error) throw error;
    return data || [];
  },

  /**
   * Pre-delete impact summary (`get_property_delete_summary` RPC — the jsonb
   * counts the delete-confirm dialog renders). RLS-scoped like the
   * pre-consolidation site; throws on error (the caller falls back to the
   * dialog without counts). Result passed through unparsed.
   */
  async getDeleteSummary(propertyId: string): Promise<unknown> {
    const { data, error } = await supabase.rpc("get_property_delete_summary", {
      p_property_id: propertyId,
    });
    if (error) throw error;
    return data;
  },

  /**
   * Cascade-delete a property (`delete_property_cascade` RPC — soft-deletes
   * the property and its dependent rows atomically in Postgres). Company-
   * scoped via p_company_id; throws on error.
   */
  async deleteCascade(propertyId: string, companyId: string): Promise<void> {
    const { error } = await supabase.rpc("delete_property_cascade", {
      p_property_id: propertyId,
      p_company_id: companyId,
    });
    if (error) throw error;
  },

  /**
   * Link a property to an owner (sets owner_id), scoped by id AND company_id.
   * Deliberately the direct Supabase write (verbatim from the
   * pre-consolidation site) — NOT the Nest-first `update()` — so the
   * consolidation stays behavior-identical; flip candidate with the
   * properties write slice.
   */
  async setOwner(
    propertyId: string,
    ownerId: string,
    companyId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from(TABLE)
      .update({ owner_id: ownerId })
      .eq("id", propertyId)
      .eq("company_id", companyId);
    if (error) throw error;
  },

  /**
   * Update a company-scoped property; returns the updated row (or null if not
   * found). FLIPPED to Nest (write-ownership gate — invalidates the cached
   * card); Supabase stays the loud fallback.
   */
  async update(id: string, patch: TablesUpdate<"properties">, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.properties.update(id), {
          method: "PUT",
          body: JSON.stringify(patch),
        });
        return propertyRowSchema.parse(row) as unknown as Awaited<
          ReturnType<typeof updateViaSupabase>
        >;
      } catch (err) {
        reportNestFallback("properties.update", err);
      }
    }
    return updateViaSupabase(id, patch, companyId);
  },

  /**
   * Soft-delete a company-scoped property (`deleted_at = now()`).
   *
   * Properties carry a `deleted_at` column and every read filters
   * `deleted_at IS NULL`, so deletion must be soft — matching the rest of the
   * app (units `is_active`, maintenance/receipts `deleted_at`). A hard delete
   * would destroy history that units/contracts/invoices still reference (and
   * could fail on those foreign keys).
   *
   * FLIPPED to Nest (DELETE /properties/:id — soft server-side, invalidates
   * the cached card); Supabase stays the loud fallback.
   */
  async softDelete(id: string, companyId: string) {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.properties.remove(id), {
          method: "DELETE",
        });
        return;
      } catch (err) {
        reportNestFallback("properties.softDelete", err);
      }
    }
    const { error } = await supabase
      .from(TABLE)
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) throw error;
  },

  /**
   * Category breakdown for the company (optionally employee-scoped).
   *
   * Buckets follow `categorizeProperty`: Land is driven by `property_type`
   * (a land plot counts as Land regardless of its purpose) plus the legacy
   * `agricultural` purpose; the rest map from `purpose`. See
   * `@/lib/propertyCategory` for the precedence rules.
   */
  async statsByPurpose({ companyId, assignedIds, startDate, endDate }: ListOptions) {
    // FLIPPED to the cached Nest summary (GET /properties/stats — Phase 3) for
    // company-wide staff reads; the employee Layer-2 scope stays on Supabase
    // (same carve-out as list()). Loud Supabase fallback. A date range bypasses
    // the cache server-side (see PropertiesService.stats) — the FE just forwards it.
    if (API_URL && assignedIds == null) {
      try {
        const stats = await nestRequestScoped<unknown>(API_ROUTES.properties.stats(startDate, endDate));
        return propertyStatsSchema.parse(stats);
      } catch (err) {
        reportNestFallback("properties.statsByPurpose", err);
      }
    }
    let query = supabase
      .from(TABLE)
      .select("purpose, property_type")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (assignedIds && assignedIds.length > 0) {
      query = query.in("id", assignedIds);
    }
    if (startDate) query = query.gte("created_at", startDate);
    if (endDate) query = query.lte("created_at", endDate);

    const { data, error } = await query;
    if (error) throw error;

    const countOf = (category: PropertyCategory) =>
      data.filter((p) => categorizeProperty(p) === category).length;

    return {
      total: data.length,
      residential: countOf("residential"),
      commercial: countOf("commercial"),
      industrial: countOf("industrial"),
      land: countOf("land"),
      mixed: countOf("mixed"),
    };
  },

  /**
   * Super-admin global property count — NOT company-scoped.
   *
   * Counts non-deleted properties across ALL companies for the super-admin
   * dashboard KPIs, optionally narrowed by `purpose`. HEAD-only count query, so
   * zero rows are transferred. This is the documented cross-company exception to
   * the company-scoping rule above: super_admin's `get_user_company_ids()` spans
   * every company, so the existing `properties` SELECT policy already permits the
   * read — the same path the super-admin dashboard already uses. Mirrors the
   * super-admin carve-outs in `invoicesRepository`.
   */
  async countBySuperAdmin(purpose?: PropertyPurpose): Promise<number> {
    let query = supabase
      .from(TABLE)
      .select("*", { count: "exact", head: true })
      .is("deleted_at", null);

    if (purpose) query = query.eq("purpose", purpose);

    const { count, error } = await query;
    if (error) {
      console.error("Error counting properties:", serializeError(error));
      throw error;
    }
    return count ?? 0;
  },

  /**
   * Super-admin global property breakdown by dashboard CATEGORY — NOT
   * company-scoped. Mirrors `statsByPurpose` (same `categorizeProperty` buckets,
   * incl. the `property_type`-driven Land bucket) but spans ALL companies for
   * the super-admin dashboard pie.
   *
   * Why this exists: the pie previously summed three raw `purpose` counts
   * (residential / commercial / multi_purpose) and silently dropped industrial,
   * agricultural and every land plot, so the slices never summed to the
   * "Properties" KPI. Categorizing every non-deleted row guarantees the buckets
   * (incl. `other`) reconcile to `total`.
   *
   * Stays on Supabase — relies on super_admin RLS spanning every company, the
   * same documented carve-out `countBySuperAdmin` uses. A HEAD count can't
   * classify rows, so this selects the two classifier columns only; `.range`
   * lifts PostgREST's 1000-row default so the platform-wide read isn't capped.
   * The fetch is bounded at 100k rows — far beyond current scale — and a cap-hit
   * is reported loudly (below) rather than silently truncating the pie. If the
   * platform ever approaches that, move this aggregation server-side.
   */
  async statsByPurposeForSuperAdmin() {
    const PAGE_CAP = 100_000;
    const { data, error } = await supabase
      .from(TABLE)
      .select("purpose, property_type")
      .is("deleted_at", null)
      .range(0, PAGE_CAP - 1);
    if (error) {
      console.error("Error counting properties by type:", serializeError(error));
      throw error;
    }

    // PostgREST silently returns at most the requested range. Hitting the cap
    // means the pie under-counts — surface it loudly (Sentry breadcrumb) instead
    // of lying with a truncated total.
    if (data.length >= PAGE_CAP) {
      console.error(
        serializeError(
          new Error(
            `statsByPurposeForSuperAdmin hit the ${PAGE_CAP}-row cap — ` +
              "property-type pie is truncated; move this aggregation server-side.",
          ),
        ),
      );
    }

    const countOf = (category: PropertyCategory) =>
      data.filter((p) => categorizeProperty(p) === category).length;

    return {
      total: data.length,
      residential: countOf("residential"),
      commercial: countOf("commercial"),
      industrial: countOf("industrial"),
      land: countOf("land"),
      mixed: countOf("mixed"),
      other: countOf("other"),
    };
  },

  /**
   * Fire-and-forget AR↔EN auto-translation for a property's free-text fields.
   * NEVER throws — the property has already been written by the caller, so a
   * translation outage must not roll back the save. The endpoint is
   * idempotent server-side; safe to call on every save.
   */
  async translate(id: string): Promise<void> {
    await fireAndForgetNestPost(
      API_ROUTES.properties.translate(id),
      "properties.translate",
    );
  },

  /**
   * The narrow property card for the task property-context sidebar (fe-tail
   * T9 — useTaskPropertyContext). Returned untyped: the hook coerces the row
   * itself (its `coerceProperty` predates types.ts carrying compound_*).
   * NEVER throws — the inline original destructured `{ data }` only, so an
   * error renders the card without a property section.
   */
  async taskContextCard(
    propertyId: string,
    companyId: string,
  ): Promise<Record<string, unknown> | null> {
    const { data } = await supabase
      .from(TABLE)
      .select(
        "id, name, name_en, latitude, longitude, address, amenities, compound_name, compound_code, owner_id, images",
      )
      .eq("id", propertyId)
      .eq("company_id", companyId)
      .maybeSingle();
    return (data as Record<string, unknown> | null) ?? null;
  },
};
